import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoaderCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import authService from "@/services/auth.service";

const formSchema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
});

export default function TbForgotPassword() {
  const form = useForm({
    resolver: yupResolver(formSchema),
    values: {
      email: "",
    },
  });

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  const submit = async (values: any) => {
    try {
      setSuccess("");
      setError("");
      await authService.forgotPassword({
        email: values.email,
      });
      form.reset();
      setSuccess("Check your inbox for reset link");
    } catch (e) {
      setError(e?.response?.data?.message || "An error occured");
      const errors = e?.response?.data?.meta?.errors || {};
      Object.keys(errors)?.forEach((field: any) => {
        form.setError(field, {
          message: errors[field],
        });
      });
    }
  };

  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-background border-b shadow-sm-">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex justify-between items-center">
          <a href="/" className="flex gap-2 items-center">
            <img className="h-8" src="/logo.svg" alt="" />
            <img className="h-[24px]" src="/logo_b.png" alt="" />
          </a>
        </div>
      </header>
      <div className="bg-gradient-to-b h-full flex-1 dark:bg-background bg-orange-50/50 flex ivtems-center justify-center p-4">
        <div className={`h-auto`}>
          {" "}
          <Card className="transition-all mt-4 sm:mt-8 mx-auto max-w-[380px]">
            <CardHeader className="space-y-1">
              <CardTitle className="text-lg text-center">
                Forgot Your Password.
              </CardTitle>
              <CardDescription className="text-center !leading-7 !text-sm">
                <span className=" !leading-7">
                  Please enter the email address you'd like your password reset
                  information sent to.
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pb-3">
              <div>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <form onSubmit={form.handleSubmit(submit)}>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="phone">Enter your email</Label>
                      <Input
                        placeholder="Enter your email"
                        value={form.watch("email")}
                        onChange={(e) => form.setValue("email", e.target.value)}
                        name="email"
                        error={form.formState?.errors["email"]?.message}
                      />
                    </div>
                  </div>
                  <Button
                    disabled={form.formState.isSubmitting}
                    onClick={form.handleSubmit(submit)}
                    className="w-full mt-4"
                    type="submit"
                  >
                    {form.formState.isSubmitting && (
                      <LoaderCircle
                        className="-ms-1 me-2 animate-spin"
                        size={16}
                      />
                    )}
                    Request reset link.
                  </Button>
                </form>
              </div>
            </CardContent>

            <CardFooter className="flex pb-6 flex-col items-center justify-center space-y-2">
              <Link to={"/login"} className="underline text-sm text-orange-500">
                Back to login.
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
      <footer className="bg-background py-4 border-t">
        <div className="max-w-6xl flex justify-center sm:justify-between mx-auto px-6 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          <p>© 2025 Table Tronic. All rights reserved.</p>
          <div className="hidden sm:flex gap-4 items-center">
            <a href="#" className="underline">
              Terms & Conditions
            </a>
            <a href="#" className="underline">
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
